import { withErrorBoundary } from 'app/components/error-boundary';
import { Box, Column, Row } from 'app/design/primitives/box';
import { SearchInput } from 'app/design/primitives/input/search';
import { Text } from 'app/design/primitives/text';

import { IconMessages } from '@tabler/icons-react-native';
import { Clubs } from 'app/design/icons/clubs';
import { PCImage } from 'app/design/primitives/image';
import { useNavigation } from 'app/hooks/navigation';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { useCreateMemberChannel } from 'app/network/chat/channel';
import { useFindUsers, type FoundUser } from 'app/network/user';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { createTheme as createDSTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, FlatList, Pressable } from 'react-native';
const theme = createDSTheme();

const UserRow = function UserRow({ user, onPress }: { user: FoundUser; onPress: (user: FoundUser) => void }) {
  const handlePress = () => onPress(user);
  const firstName = user.profile?.firstName ?? '';
  const lastName = user.profile?.lastName ?? '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return (
    <Pressable onPress={handlePress}>
      <Row
        alignItems="center"
        gap="spacing.general.x4"
        paddingHorizontal="spacing.general.x8"
        paddingVertical="spacing.general.x2"
      >
        {user.profile?.picture ? (
          <PCImage
            aspectRatio={1}
            cachePolicy="memory-disk"
            height={44}
            recyclingKey={`${user.profile.picture}-new-conv`}
            source={{ uri: getImageVariantUrl(user.profile.picture, 'mini') }}
            style={{ borderRadius: 22 }}
          />
        ) : (
          <Box
            alignItems="center"
            backgroundColor="surface.disabled"
            borderRadius="radius.xl"
            height={44}
            justifyContent="center"
            width={44}
          />
        )}
        <Column flex={1}>
          <Text fontWeight="600" numberOfLines={1}>
            {fullName}
          </Text>
          {user.addresses?.[0]?.city ? (
            <Text color="text.secondary" fontSize={13} numberOfLines={1}>
              {user.addresses[0].city}
            </Text>
          ) : null}
        </Column>
      </Row>
    </Pressable>
  );
};

const NewConversationScreenComponent: NavigationScreen<'new-conversation'> = ({ navigation: _navigation }) => {
  const { t } = useTranslation('new-conversation');

  const navigation = useNavigation<'new-conversation'>({
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
        {t('title')}
      </Text>
    ),
  });

  const [rawSearch, setRawSearch] = useState('');

  const { mutate: createMemberChannel, isPending: isCreating } = useCreateMemberChannel({
    onSuccess: (channel) => {
      navigation.goBack();
      navigateToChat(navigation, channel.channel_url);
    },
  });

  const [choseDM, setChoseDM] = useState(false);

  const canSearch = rawSearch.trim().length >= 3;

  const {
    data,
    isPending: isSearching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFindUsers(
    { search: rawSearch.trim(), groupIds: ['nearby'], sortBy: 'relevance', canReceiveInAppContact: true },
    { enabled: canSearch },
  );

  const results = canSearch ? (data?.pages.flatMap((p) => p.results) ?? []) : [];

  const handleSelectUser = (user: FoundUser) => {
    if (isCreating) return;
    createMemberChannel({ id: user.id });
  };

  const handleGroupChat = () => {
    navigation.navigate('new-group-chat', undefined, { pop: true });
  };

  const renderItem = ({ item }: { item: FoundUser }) => <UserRow user={item} onPress={handleSelectUser} />;

  const keyExtractor = (item: FoundUser) => item.id;
  const handleEndReached = () => {
    if (hasNextPage && !isSearching && !isFetchingNextPage) void fetchNextPage();
  };

  return (
    <Column flex={1} backgroundColor="surface.default">
      <Column
        paddingHorizontal="spacing.general.x4"
        paddingTop="spacing.general.x4"
        paddingBottom="spacing.general.x2"
        gap="spacing.general.x4"
      >
        {!choseDM ? (
          <Column>
            <Pressable onPress={() => setChoseDM(true)}>
              <Row alignItems="center" gap="spacing.general.x3" paddingVertical="spacing.general.x1">
                <Box
                  alignItems="center"
                  backgroundColor="surface.disabled"
                  borderRadius="radius.xl"
                  height={50}
                  justifyContent="center"
                  width={50}
                >
                  <IconMessages color={theme.colors['text.primary']} size={20} />
                </Box>
                <Text fontWeight="600">{t('dmChat')}</Text>
              </Row>
            </Pressable>

            <Pressable onPress={handleGroupChat}>
              <Row alignItems="center" gap="spacing.general.x3" paddingVertical="spacing.general.x1">
                <Box
                  alignItems="center"
                  backgroundColor="surface.disabled"
                  borderRadius="radius.xl"
                  height={50}
                  justifyContent="center"
                  width={50}
                >
                  <Clubs color={theme.colors['text.primary']} height={25} width={25} />
                </Box>
                <Text fontWeight="600">{t('groupChat')}</Text>
              </Row>
            </Pressable>
          </Column>
        ) : null}

        {choseDM ? <SearchInput onSubmit={setRawSearch} submitOnEmptyChange /> : null}
      </Column>

      {canSearch && (isSearching || (results.length === 0 && isFetchingNextPage)) ? (
        <Column alignItems="center" paddingVertical="spacing.general.x8">
          <ActivityIndicator />
        </Column>
      ) : canSearch && results.length === 0 && !isFetchingNextPage ? (
        <Column alignItems="center" paddingHorizontal="spacing.general.x8" paddingVertical="spacing.general.x8">
          <Text color="text.secondary" textAlign="center">
            {t('noResults')}
          </Text>
        </Column>
      ) : (
        <FlatList
          data={results}
          keyExtractor={keyExtractor}
          keyboardShouldPersistTaps="handled"
          onEndReached={handleEndReached}
          onEndReachedThreshold={0.4}
          renderItem={renderItem}
        />
      )}
    </Column>
  );
};

export const NewConversationScreen: NavigationScreen<'new-conversation'> = withOptionalMetrics(
  'NewConversationScreen',
  withErrorBoundary(NewConversationScreenComponent),
);
